import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { formatTimeAgo, formatNumber, getInitials, cn } from '../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { 
  Heart, MessageCircle, Share2, Volume2, VolumeX, 
  ChevronLeft, Play, Pause, MoreHorizontal, Crown, BadgeCheck,
  Home, Eye
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Single Video Card Component (Full Screen)
const VideoCard = ({ post, isActive, isMuted, onToggleMute, onLike, onComment, onShare }) => {
  const { user, token, language } = useAuth();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLiked, setIsLiked] = useState(post.is_liked);
  const [likesCount, setLikesCount] = useState(post.likes_count);
  const [viewsCount, setViewsCount] = useState(post.views_count || 0);
  const [liking, setLiking] = useState(false);
  const viewTrackedRef = useRef(false);

  // Play/pause based on active state + view tracking
  useEffect(() => {
    if (videoRef.current) {
      if (isActive) {
        videoRef.current.play().catch(() => {});
        setIsPlaying(true);
        // Track view as soon as this reel is the active one — the user
        // intentionally swiped to it.
        if (!viewTrackedRef.current && post.user_id !== user?.id && token) {
          viewTrackedRef.current = true;
          axios.post(`${API}/posts/${post.id}/view`, {}, {
            headers: { Authorization: `Bearer ${token}` },
          }).then((r) => {
            if (r.data?.counted) {
              setViewsCount((c) => Math.max(c, r.data.views_count || (c + 1)));
            }
          }).catch(() => { viewTrackedRef.current = false; });
        }
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  }, [isActive, post.id, post.user_id, user?.id, token]);

  // Update mute state
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play().catch(() => {});
        setIsPlaying(true);
      }
    }
  };

  const handleLike = async (e) => {
    e.stopPropagation();
    if (!user || liking) return;
    
    setLiking(true);
    const wasLiked = isLiked;
    
    // Optimistic update
    setIsLiked(!wasLiked);
    setLikesCount(prev => wasLiked ? prev - 1 : prev + 1);
    
    try {
      await axios.post(`${API}/posts/${post.id}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Revert on error
      setIsLiked(wasLiked);
      setLikesCount(prev => wasLiked ? prev + 1 : prev - 1);
    } finally {
      setLiking(false);
    }
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/post/${post.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${post.display_name || post.username} on FityH`,
          text: post.content || 'Check out this video!',
          url: shareUrl
        });
      } catch (e) {}
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied!');
    }
  };

  const isCreator = post.is_creator || post.user_type === 'creator';
  const isVerified = post.is_verified || post.verified;

  return (
    <div className="relative w-full h-full bg-black snap-start snap-always">
      {/* Video */}
      <video
        ref={videoRef}
        src={post.media[0]?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${post.media[0]}` : post.media[0]}
        className="absolute inset-0 w-full h-full object-cover"
        loop
        playsInline
        muted={isMuted}
        onClick={togglePlay}
        data-testid={`reels-video-${post.id}`}
      />

      {/* Play/Pause Indicator */}
      {!isPlaying && (
        <div 
          className="absolute inset-0 flex items-center justify-center bg-black/20"
          onClick={togglePlay}
        >
          <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Play className="w-10 h-10 text-white ml-1" />
          </div>
        </div>
      )}

      {/* Right Side Actions */}
      <div className="absolute right-3 bottom-32 flex flex-col items-center gap-5">
        {/* User Avatar */}
        <button 
          onClick={() => navigate(`/profile/${post.username}`)}
          className="relative"
        >
          <Avatar className="w-12 h-12 ring-2 ring-white">
            <AvatarImage src={post.avatar} alt={post.display_name} />
            <AvatarFallback className="bg-gradient-to-br from-pink-500 to-violet-500 text-white font-semibold">
              {getInitials(post.display_name || post.username)}
            </AvatarFallback>
          </Avatar>
          {isCreator && (
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-pink-500 flex items-center justify-center ring-2 ring-black">
              <Crown className="w-3 h-3 text-white" />
            </div>
          )}
        </button>

        {/* Like */}
        <button
          onClick={handleLike}
          className="flex flex-col items-center gap-1"
          data-testid={`reels-like-${post.id}`}
        >
          <div className={cn(
            "w-12 h-12 rounded-full flex items-center justify-center transition-all",
            isLiked ? "bg-pink-500" : "bg-white/10 backdrop-blur-sm"
          )}>
            <Heart className={cn(
              "w-6 h-6 text-white transition-transform",
              isLiked && "fill-white scale-110",
              liking && "animate-pulse"
            )} />
          </div>
          <span className="text-white text-xs font-medium">{formatNumber(likesCount)}</span>
        </button>

        {/* Comment */}
        <button
          onClick={() => navigate(`/post/${post.id}`)}
          className="flex flex-col items-center gap-1"
          data-testid={`reels-comment-${post.id}`}
        >
          <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
            <MessageCircle className="w-6 h-6 text-white" />
          </div>
          <span className="text-white text-xs font-medium">{formatNumber(post.comments_count)}</span>
        </button>

        {/* Share */}
        <button
          onClick={handleShare}
          className="flex flex-col items-center gap-1"
          data-testid={`reels-share-${post.id}`}
        >
          <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
            <Share2 className="w-6 h-6 text-white" />
          </div>
          <span className="text-white text-xs font-medium">Share</span>
        </button>

        {/* Views */}
        <div
          className="flex flex-col items-center gap-1"
          data-testid={`reels-views-${post.id}`}
        >
          <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
            <Eye className="w-6 h-6 text-white" />
          </div>
          <span className="text-white text-xs font-medium">{formatNumber(viewsCount)}</span>
        </div>

        {/* Mute/Unmute */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleMute(); }}
          className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center"
        >
          {isMuted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
        </button>
      </div>

      {/* Bottom Info */}
      <div className="absolute bottom-4 left-3 right-20 z-10">
        {/* Username */}
        <button 
          onClick={() => navigate(`/profile/${post.username}`)}
          className="flex items-center gap-2 mb-2"
        >
          <span className="text-white font-bold text-base">
            @{post.username}
          </span>
          {isVerified && <BadgeCheck className="w-4 h-4 text-pink-500" />}
          {isCreator && !isVerified && <Crown className="w-4 h-4 text-amber-500" />}
        </button>

        {/* Caption */}
        {post.content && (
          <p className="text-white text-sm line-clamp-2 mb-2">
            {post.content}
          </p>
        )}

        {/* Time */}
        <span className="text-white/60 text-xs">
          {formatTimeAgo(post.created_at)}
        </span>
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none" />
    </div>
  );
};

// Main Reels Page
export default function ReelsPage() {
  const { user, token, language } = useAuth();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(true);

  // Fetch video posts
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`${API}/posts/feed?page=1&limit=50`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        // Filter only video posts
        const videoPosts = (response.data.posts || []).filter(
          post => post.media_type === 'video' && post.media && post.media.length > 0
        );
        
        setVideos(videoPosts);
      } catch (error) {
        console.error('Failed to fetch videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, [token]);

  // Handle scroll to detect active video
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;
    
    const scrollTop = containerRef.current.scrollTop;
    const viewportHeight = containerRef.current.clientHeight;
    const newIndex = Math.round(scrollTop / viewportHeight);
    
    if (newIndex !== activeIndex && newIndex >= 0 && newIndex < videos.length) {
      setActiveIndex(newIndex);
    }
  }, [activeIndex, videos.length]);

  // Toggle mute for all videos
  const toggleMute = () => setIsMuted(prev => !prev);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="fixed inset-0 bg-black flex flex-col items-center justify-center text-white">
        <Play className="w-16 h-16 mb-4 text-pink-500" />
        <h2 className="text-xl font-bold mb-2">
          {language === 'ht' ? 'Pa gen videyo' : 'No videos yet'}
        </h2>
        <p className="text-white/60 mb-6">
          {language === 'ht' ? 'Videyo yo ap parèt isit la' : 'Videos will appear here'}
        </p>
        <Button onClick={() => navigate('/')} className="rounded-full">
          <Home className="w-4 h-4 mr-2" />
          {language === 'ht' ? 'Retounen Lakay' : 'Go Home'}
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4 bg-gradient-to-b from-black/60 to-transparent">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate(-1)}
          className="text-white hover:bg-white/10 rounded-full"
        >
          <ChevronLeft className="w-6 h-6" />
        </Button>
        <h1 className="text-white font-bold text-lg">Reels</h1>
        <div className="w-10" /> {/* Spacer */}
      </div>

      {/* Video Container */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="h-full overflow-y-scroll snap-y snap-mandatory scrollbar-hide"
        style={{ scrollSnapType: 'y mandatory' }}
      >
        {videos.map((video, index) => (
          <div 
            key={video.id} 
            className="h-screen w-full"
            style={{ scrollSnapAlign: 'start' }}
          >
            <VideoCard
              post={video}
              isActive={index === activeIndex}
              isMuted={isMuted}
              onToggleMute={toggleMute}
            />
          </div>
        ))}
      </div>

      {/* Bottom Navigation Indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-1">
        {videos.slice(0, 5).map((_, i) => (
          <div 
            key={i}
            className={cn(
              "w-1.5 h-1.5 rounded-full transition-all",
              i === activeIndex ? "bg-white w-4" : "bg-white/40"
            )}
          />
        ))}
        {videos.length > 5 && (
          <span className="text-white/60 text-xs ml-1">+{videos.length - 5}</span>
        )}
      </div>
    </div>
  );
}
